import { BodyText } from '@/components/BodyText';
import { ExpandableItem } from './ExpandableItem';
import { Box, Button, TableBody } from '@mui/material';
import { Download } from '@mui/icons-material';
import { getSampleMetadataTemplateData } from '@/pages/MetadataBulkUploadPage/components/sampleTemplateData';
import { downloadSpreadsheet, DownloadFormat } from '@/utils/downloadUtils';
import {
  StyledTable,
  StyledTableHeadRow,
  StyledTableHeadCell,
  StyledTableRow,
  StyledTableCell,
  TableHead,
} from './HelpTable';
import { HelpLink } from './HelpLink';

const downloadSampleTemplate = (format: DownloadFormat) => {
  const sampleData = getSampleMetadataTemplateData();
  const filename = `metadata-template.${format}`;

  downloadSpreadsheet(sampleData, { filename, format });
};

export const BulkDataImportHelpView = ({ isOpen, onToggle }: { isOpen: boolean; onToggle: () => void }) => {
  return (
    <ExpandableItem
      isOpen={isOpen}
      onToggle={onToggle}
      title={<>4. Connecting data from other systems</>}
      description={
        <>
          <BodyText component='p' sx={{ marginBottom: '12px' }}>
            <HelpLink to='metadata-upload'>Upload metadata</HelpLink> for parts and assemblies from your PLM, ERP, PDM,
            or other supply chain systems by uploading a CSV or XLSX file. The requirements below ensure data is applied
            to the correct parts and assemblies.
          </BodyText>
          <Box
            sx={{
              marginBottom: '12px',
              display: 'flex',
              gap: 1,
              alignItems: 'center',
            }}
          >
            <BodyText>Download sample templates to get started</BodyText>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                size='small'
                variant='outlined'
                onClick={() => downloadSampleTemplate('csv')}
                startIcon={<Download />}
              >
                CSV
              </Button>
              <Button
                size='small'
                variant='outlined'
                onClick={() => downloadSampleTemplate('xlsx')}
                startIcon={<Download />}
              >
                XLSX
              </Button>
            </Box>
          </Box>
          <StyledTable size='small' sx={{ marginTop: '12px', marginBottom: '12px' }}>
            <TableHead>
              <StyledTableHeadRow>
                <StyledTableHeadCell>Requirement</StyledTableHeadCell>
                <StyledTableHeadCell>Description</StyledTableHeadCell>
              </StyledTableHeadRow>
            </TableHead>
            <TableBody>
              <StyledTableRow>
                <StyledTableCell>
                  <BodyText color='text.primary'>Required &quot;path&quot; Column</BodyText>
                </StyledTableCell>
                <StyledTableCell>
                  <BodyText color='text.primary'>
                    The &quot;path&quot; indicates which file this data is for. Example: &quot;My
                    catalog/assembly/part1.sldprt&quot;
                  </BodyText>
                </StyledTableCell>
              </StyledTableRow>
              <StyledTableRow isEven>
                <StyledTableCell>
                  <BodyText color='text.primary'>Optional &quot;id&quot; Column</BodyText>
                </StyledTableCell>
                <StyledTableCell>
                  <BodyText color='text.primary'>
                    A Physna asset &quot;id&quot; is optional, but is the most accurate identifier.
                  </BodyText>
                </StyledTableCell>
              </StyledTableRow>
              <StyledTableRow>
                <StyledTableCell>
                  <BodyText color='text.primary'>Metadata Columns</BodyText>
                </StyledTableCell>
                <StyledTableCell>
                  <BodyText color='text.primary'>
                    Metadata columns start with &quot;metadata:&quot;
                    <br />
                    For example: &quot;metadata:Material&quot;, &quot;metadata:Color&quot;
                  </BodyText>
                </StyledTableCell>
              </StyledTableRow>
              <StyledTableRow isEven>
                <StyledTableCell>
                  <BodyText color='text.primary'>Data Format</BodyText>
                </StyledTableCell>
                <StyledTableCell>
                  <BodyText color='text.primary'>
                    Each row represents one part or assembly with its metadata values
                  </BodyText>
                </StyledTableCell>
              </StyledTableRow>
              <StyledTableRow>
                <StyledTableCell>
                  <BodyText color='text.primary'>Empty Cells</BodyText>
                </StyledTableCell>
                <StyledTableCell>
                  <BodyText color='text.primary'>
                    Empty cells will be ignored (no update will be made for that field)
                  </BodyText>
                </StyledTableCell>
              </StyledTableRow>
              <StyledTableRow isEven>
                <StyledTableCell>
                  <BodyText color='text.primary'>File Size</BodyText>
                </StyledTableCell>
                <StyledTableCell>
                  <BodyText color='text.primary'>Maximum file size: 10MB</BodyText>
                </StyledTableCell>
              </StyledTableRow>
            </TableBody>
          </StyledTable>
        </>
      }
    />
  );
};
